import React, {
  createContext,
  useState,
  useContext,
  useRef,
  useEffect,
} from "react";
import orderBatchesApi from "../apis/orderBatches";
import { v4 as uuidv4 } from "uuid";
import { message } from "antd";

export const UploadFileContext = createContext();

export const UploadProvider = ({ children }) => {
  const [progress, setProgress] = useState(0);
  const [totalUploaded, setTotalUploaded] = useState(0);
  const [totalFiles, setTotalFiles] = useState(0);
  const [uploadedFolders, setUploadedFolders] = useState([]);
  const [startUploading, setStartUploading] = useState(false);
  const { getPresignedUrlBulkUpload, createFiles } = orderBatchesApi;

  const uploadedFoldersRef = useRef(uploadedFolders);

  useEffect(() => {
    uploadedFoldersRef.current = uploadedFolders;
  }, [uploadedFolders]);

  const startUpload = async (files, batchId) => {
    setStartUploading(true);
    setProgress(0);
    setTotalFiles(files.length);
    const allowedExtensions = ["jpg", "jpeg", "webp", "png"];
    const validFiles = Array.from(files).filter((file) => {
      const fileExtension = file.name.split(".").pop().toLowerCase();
      return allowedExtensions.includes(fileExtension);
    });

    if (validFiles.length === 0) {
      message.error("No valid image files to upload.");
      setStartUploading(false);
      return;
    }
    if (validFiles.length > 20) {
      console.log("warning");
      message.warning(
        `You are uploading a folder with ${validFiles?.length} files. Please ensure that you only upload folders that contain images of a single article/product.`,
        8
      );
    }
    const chunkSize = 10;
    let uploadedFile = 0;
    let folderId = null;
    const folderName =
      validFiles[0].webkitRelativePath || validFiles[0].relativePath
        ? validFiles[0].webkitRelativePath.split("/")[0] ||
        validFiles[0].relativePath.split("/")[1]
        : "Unknown Folder";
    const totalMemoryBytes = validFiles.reduce(
      (total, file) => total + file.size,
      0
    );
    if (totalMemoryBytes === 0) {
      message.error("No valid image files in the folder to upload.");
      return;
    }

    const totalMemoryMB = (totalMemoryBytes / (1024 * 1024)).toFixed(2);

    const newFolder = {
      batchId: batchId,
      folderId: null,
      folderName: folderName,
      memory: {
        uploading: true,
        progress: 0,
        totalUploaded: 0,
        totalFiles: validFiles.length,
        totalMemory: totalMemoryMB,
        paused: false,
      },
    };

    setUploadedFolders((prev) => [...prev, newFolder]);

    const chunks = [];
    for (let i = 0; i < validFiles.length; i += chunkSize) {
      chunks.push(validFiles.slice(i, i + chunkSize));
    }

    for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex++) {
      const chunk = chunks[chunkIndex];
      const isLastChunk = chunkIndex === chunks.length - 1;

      const currentFolderState = uploadedFoldersRef.current.find(
        (folder) => folder.folderName === newFolder.folderName
      );
      if (currentFolderState?.memory.paused) {
        return;
      }

      console.log('chunkFiles', chunk);
      const payloadFiles = chunk.map((file) => ({
        filePath: file.webkitRelativePath || file.relativePath,
        mimeType: file.type,
      }));

      const payload = {
        batchId: batchId,
        files: payloadFiles,
        uploadId: folderId,
      };

      const res = await getPresignedUrlBulkUpload(payload);

      if (res.status === 200) {
        setStartUploading(false);
        if (!folderId) {
          folderId = res.data.uploadId;
          setUploadedFolders((prev) =>
            prev.map((folder) =>
              folder.folderName === newFolder.folderName
                ? { ...folder, folderId }
                : folder
            )
          );
        }

        const uploadPromises = res.data.files.map(async (data) => {
          const { url } = data.uploadObject;
          let uploadFile;

          for (let i = 0; i < chunk.length; i++) {
            if (
              chunk[i].webkitRelativePath?.replace(/^\/+/, "") ===
              data.filePath?.replace(/^\/+/, "") ||
              chunk[i].relativePath?.replace(/^\/+/, "") ===
              data.filePath?.replace(/^\/+/, "")
            ) {
              uploadFile = chunk[i];
              break;
            }
          }

          const updatedFolderState = uploadedFoldersRef.current.find(
            (folder) => folder.folderId === folderId
          );

          if (updatedFolderState?.memory.paused) {
            return;
          }

          await fetch(url, {
            method: "PUT",
            headers: {
              "Content-Type": data.mimeType,
            },
            body: uploadFile,
          });

          uploadedFile++;
          const newProgress = (
            (uploadedFile / validFiles.length) *
            100
          ).toFixed(2);
          setUploadedFolders((prev) =>
            prev.map((folder) =>
              folder.folderId === folderId
                ? {
                  ...folder,
                  memory: {
                    ...folder.memory,
                    totalUploaded: uploadedFile,
                    progress: newProgress,
                  },
                }
                : folder
            )
          );
          setProgress(newProgress);
          setTotalUploaded(uploadedFile);

          return data.uploadObject.key;
        });

        const fileImages = await Promise.all(uploadPromises);

        const folderState = uploadedFoldersRef.current.find(
          (folder) => folder.folderId === folderId
        );

        if (!folderState?.memory.paused) {
          const createFilePayload = {
            images: fileImages,
            batch: batchId,
            uploadId: folderId,
            isCompleted: isLastChunk || undefined,
            uploadMetadata: isLastChunk
              ? {
                folderName: folderState?.folderName,
                foldersize: folderState?.memory?.totalMemory,
              }
              : undefined,
          };

          await createFiles(createFilePayload);
        }
      } else {
        console.error("Failed to get presigned URLs for batch");
      }
    }

    setUploadedFolders((prev) =>
      prev.map((folder) =>
        folder.folderId === folderId
          ? { ...folder, memory: { ...folder.memory, uploading: false } }
          : folder
      )
    );
  };

  const pauseUpload = (folderId) => {
    setUploadedFolders((prev) => {
      return prev.map((folder) =>
        folder.folderId === folderId && folder.memory.uploading
          ? { ...folder, memory: { ...folder.memory, paused: true } }
          : folder
      );
    });
  };

  const resumeUpload = (folderId) => {
    setUploadedFolders((prev) => {
      return prev.map((folder) =>
        folder.folderId === folderId && folder.memory.uploading
          ? { ...folder, memory: { ...folder.memory, paused: false } }
          : folder
      );
    });
  };

  return (
    <UploadFileContext.Provider
      value={{
        progress,
        startUpload,
        totalFiles,
        totalUploaded,
        pauseUpload,
        resumeUpload,
        uploadedFolders,
        setUploadedFolders,
        startUploading,
        setStartUploading,
      }}
    >
      {children}
    </UploadFileContext.Provider>
  );
};

export const useUpload = () => useContext(UploadFileContext);
